import { AreaChart, Area, XAxis, YAxis, ResponsiveContainer, Tooltip, ReferenceLine } from 'recharts';
import type { SimulacaoInvestimento } from '../../types.d';
import { 
  CORES_SITE, 
  TIPOGRAFIA, 
  CONTAINERS, 
  ESPACAMENTO 
} from '../../data/visualConstants';

interface GraficoInvestimentoProps {
  simulacao: SimulacaoInvestimento;
}

interface DadoInvestimento {
  ano: number;
  montanteTotal: number;
  aporteAcumulado: number;
  rendimentoAcumulado: number;
}

const formatarMoeda = (valor: number): string => {
  return `R$ ${(valor / 1000).toFixed(0)}k`;
};

const formatarMoedaCompleta = (valor: number): string => {
  return `R$ ${valor.toLocaleString('pt-BR')}`;
};

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className={`${CONTAINERS.CARD_BASE} ${ESPACAMENTO.CARD_PADDING_PEQUENO} shadow-lg`}>
        <p className={TIPOGRAFIA.LABEL_MEDIO}>{`Ano ${data.ano}`}</p>
        <p className="text-sm text-green-600 font-semibold">
          Total: {formatarMoedaCompleta(data.montanteTotal)}
        </p>
        <p className={`${TIPOGRAFIA.LEGENDA} text-gray-700`}>
          Aportes: {formatarMoedaCompleta(data.aporteAcumulado)}
        </p>
        <p className={`${TIPOGRAFIA.LEGENDA} text-orange-600`}>
          Rendimentos: {formatarMoedaCompleta(data.rendimentoAcumulado)}
        </p>
      </div>
    );
  }
  return null;
};

export const GraficoInvestimento = ({ simulacao }: GraficoInvestimentoProps) => {
  
  const gerarDadosTempo = (): DadoInvestimento[] => {
    const dados: DadoInvestimento[] = [];
    const rentabilidadeAnual = 0.08;
    const rentabilidadeMensal = Math.pow(1 + rentabilidadeAnual, 1/12) - 1;
    
    for (let ano = 1; ano <= 30; ano++) {
      const meses = ano * 12;
      
      const montanteTotal = simulacao.aporteMenusal * ((Math.pow(1 + rentabilidadeMensal, meses) - 1) / rentabilidadeMensal);
      const aporteAcumulado = simulacao.aporteMenusal * meses;
      const rendimentoAcumulado = montanteTotal - aporteAcumulado;
      
      dados.push({
        ano,
        montanteTotal: Math.round(montanteTotal),
        aporteAcumulado,
        rendimentoAcumulado: Math.round(rendimentoAcumulado)
      });
    }
    
    return dados;
  };

  const dadosGrafico = gerarDadosTempo();
  
  // Marcos importantes (10, 25, 30 anos)
  const marcos = [
    { ano: 10, valor: simulacao.em10Anos, label: '10 anos' },
    { ano: 25, valor: simulacao.em25Anos, label: '25 anos' },
    { ano: 30, valor: simulacao.em30Anos, label: '30 anos' }
  ];

  return (
    <div className={`${CONTAINERS.SECAO_FUNDO_NEUTRO} ${ESPACAMENTO.CARD_PADDING} ${ESPACAMENTO.GRAFICO_MARGIN} relative`}>
      <h3 className={TIPOGRAFIA.TITULO_SECAO}>
        Simulação de Investimento
      </h3>
      <p className={`${TIPOGRAFIA.CORPO_SECUNDARIO} text-center mb-4`}>
        Investindo 25% do seu salário (R$ {simulacao.aporteMenusal.toLocaleString('pt-BR')}/mês):
      </p>
      
      {/* Container do gráfico com posicionamento relativo */}
      <div className="relative">
        {/* Label do eixo Y - oculto no mobile */}
        <div className="hidden sm:block absolute left-2 top-1/2 transform -rotate-90 origin-center text-sm font-medium text-gray-600">
          Valor (R$)
        </div>
        
        {/* Gráfico temporal - sem margin left no mobile */}
        <div className="h-80 w-full sm:ml-8">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={dadosGrafico}
              margin={{ top: 10, right: 30, left: 40, bottom: 40 }}
            >
              <XAxis 
                dataKey="ano" 
                fontSize={14}
                tick={{ fill: '#374151' }}
              />
              <YAxis 
                tickFormatter={formatarMoeda}
                fontSize={14}
                tick={{ fill: '#374151' }}
              />
              <Tooltip content={<CustomTooltip />} />
              
              {/* Linhas de referência nos marcos */}
              {marcos.map(marco => (
                <ReferenceLine 
                  key={marco.ano}
                  x={marco.ano} 
                  stroke={CORES_SITE.CINZA_BORDER}
                  strokeDasharray="3 3"
                />
              ))}
              
              {/* Área dos aportes */}
              <Area
                type="monotone"
                dataKey="aporteAcumulado"
                stackId="1"
                stroke={CORES_SITE.VERMELHO_PRIMARY}
                fill={CORES_SITE.VERMELHO_PRIMARY}
                fillOpacity={0.6}
              />
              
              {/* Área dos rendimentos */}
              <Area
                type="monotone"
                dataKey="rendimentoAcumulado"
                stackId="1"
                stroke={CORES_SITE.AMARELO_DESTAQUE}
                fill={CORES_SITE.AMARELO_DESTAQUE}
                fillOpacity={0.7}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        
        {/* Label do eixo X */}
        <div className="text-center mt-2">
          <p className={TIPOGRAFIA.LABEL_PEQUENO}>Anos</p>
        </div>
      </div>
      
      {/* Cards dos marcos */}
      <div className="grid sm:grid-cols-3 gap-4 mt-6">
        {marcos.map((marco) => (
          <div 
            key={marco.ano} 
            className={`${CONTAINERS.CARD_BASE} ${ESPACAMENTO.CARD_PADDING_PEQUENO} text-center`}
          >
            <p className={TIPOGRAFIA.LABEL_PEQUENO}>{marco.label}</p>
            <p className="text-xl font-bold text-green-600 mt-1">
              {formatarMoedaCompleta(marco.valor)}
            </p>
          </div>
        ))}
      </div>
      
      {/* Legenda e disclaimer */}
      <div className="mt-6 space-y-3">
        <div className="flex justify-center items-center gap-6 text-sm">
          <div className="flex items-center gap-2">
            <div 
              className="w-4 h-4 rounded" 
              style={{ backgroundColor: CORES_SITE.VERMELHO_PRIMARY }}
            ></div>
            <span className={TIPOGRAFIA.CORPO}>Aportes acumulados</span>
          </div>
          <div className="flex items-center gap-2">
            <div 
              className="w-4 h-4 rounded" 
              style={{ backgroundColor: CORES_SITE.AMARELO_DESTAQUE }}
            ></div>
            <span className={TIPOGRAFIA.CORPO}>Rendimentos</span>
          </div>
        </div>
        
      </div>
    </div>
  );
};